/**
 * Shared report filter UI — template slot builder and runtime filter controls.
 */
import React from 'react';

import {
  UniCheckField, UniDateField, UniSelectField, UniTextField,
} from './UniShared.jsx';

export const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom dates' },
];

export const FILTER_SLOT_TYPE_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'choice', label: 'Choice' },
  { value: 'user_attr', label: 'User attribute' },
  { value: 'text', label: 'Text' },
  { value: 'numeric_range', label: 'Numeric range' },
  { value: 'latest_only', label: 'Latest per user' },
];

export function filterQueryString(filterValues) {
  const parts = [];
  Object.entries(filterValues || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    parts.push(`f.${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  });
  return parts.join('&');
}

export function isIsoDate(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function dateFieldValueForSlot(slot) {
  if (slot.question_id) return `q:${slot.question_id}`;
  return slot.field || 'submitted_at';
}

function dateFieldOptions(catalog) {
  return (catalog?.date_fields || [
    { value: 'submitted_at', label: 'Submission date' },
  ]).map((f) => ({
    value: f.value,
    label: f.label,
    question_id: f.question_id,
  }));
}

function slotColumnKey(slot) {
  if (slot.column_key) return slot.column_key;
  if (slot.type === 'date') {
    if (slot.question_id) return `q:${slot.question_id}`;
    if (slot.field && slot.field.startsWith('q:')) return slot.field;
    return slot.field === 'submitted_at' ? 'submitted_at' : (slot.field || 'submitted_at');
  }
  if (slot.type === 'user_attr' && slot.field) return `user.${slot.field}`;
  if (slot.question_id) return `q:${slot.question_id}`;
  return '';
}

export function slotFromFilterableColumn(col, existingIds = []) {
  const baseId = (col.column_key || 'filter')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^user_/, 'user_')
    .slice(0, 32);
  let id = baseId;
  let n = 2;
  while (existingIds.includes(id)) {
    id = `${baseId}_${n}`;
    n += 1;
  }
  const slot = {
    id,
    type: col.filter_type,
    label: col.label,
    column_key: col.column_key,
  };
  if (col.field) slot.field = col.field;
  if (col.question_id) slot.question_id = col.question_id;
  if (col.op) slot.op = col.op;
  if (col.filter_type === 'date' && !col.question_id) {
    slot.field = col.field || 'submitted_at';
  }
  return slot;
}

export function renderDateFilterSlot(slot, values, onChange, catalog) {
  const id = slot.id;
  const columnHint = slot.column_key || slot.label || id;
  const label = slot.label
    ? `${slot.label}${slot.question_id ? ' (form date)' : ''}`
    : columnHint;

  if (slot.single_day) {
    const preset = isIsoDate(values[id]) ? '' : (values[id] || slot.default || 'today');
    return (
      <React.Fragment key={id}>
        <UniSelectField
          label={label}
          value={preset}
          onChange={(v) => onChange(id, v)}
          options={catalog?.date_presets || DATE_RANGE_OPTIONS}
        />
        <UniDateField
          label="Or pick a date"
          value={isIsoDate(values[id]) ? values[id] : ''}
          onChange={(v) => onChange(id, v)}
        />
      </React.Fragment>
    );
  }
  return (
    <React.Fragment key={id}>
      <UniSelectField
        label={label}
        value={values[id] || ''}
        onChange={(v) => onChange(id, v)}
        options={[
          { value: '', label: '— any —' },
          ...(catalog?.date_presets || DATE_RANGE_OPTIONS),
        ]}
      />
      {values[id] === 'custom' && (
        <>
          <UniDateField
            label="From"
            value={values[`${id}_from`] || ''}
            onChange={(v) => onChange(`${id}_from`, v)}
          />
          <UniDateField
            label="To"
            value={values[`${id}_to`] || ''}
            onChange={(v) => onChange(`${id}_to`, v)}
          />
        </>
      )}
    </React.Fragment>
  );
}

export function ReportRuntimeFilters({ slots, values, onChange, catalog }) {
  if (!slots?.length) return null;
  return (
    <div className="uni-card uni-report-filters">
      <div className="uni-card__title">Report filters</div>
      <div className="uni-card__sub">
        Filter rows by column values. Change these and press Refresh to update the table.
      </div>
      <div className="uni-filter-slot-list">
        {slots.map((slot) => {
          const id = slot.id;
          let fields;
          if (slot.type === 'date') {
            fields = renderDateFilterSlot(slot, values, onChange, catalog);
          } else if (slot.type === 'user_attr') {
            fields = (
              <UniTextField
                label={slot.label || `User ${slot.field || ''}`}
                value={values[id] || ''}
                onChange={(v) => onChange(id, v)}
                placeholder="Exact match (e.g. city name)"
              />
            );
          } else if (slot.type === 'numeric_range') {
            fields = (
              <>
                <UniTextField
                  label={`${slot.label || id} (min)`}
                  value={values[`${id}_min`] || values[id] || ''}
                  onChange={(v) => onChange(`${id}_min`, v)}
                  placeholder="Minimum"
                />
                <UniTextField
                  label={`${slot.label || id} (max)`}
                  value={values[`${id}_max`] || ''}
                  onChange={(v) => onChange(`${id}_max`, v)}
                  placeholder="Maximum"
                />
              </>
            );
          } else if (slot.type === 'latest_per_user' || slot.type === 'latest_only') {
            fields = (
              <UniCheckField
                label={slot.label || 'Latest response per user only'}
                checked={values[id] === '1' || values[id] === 'true'}
                onChange={(v) => onChange(id, v ? '1' : '')}
              />
            );
          } else {
            fields = (
              <UniTextField
                label={slot.label || id}
                value={values[id] || ''}
                onChange={(v) => onChange(id, v)}
                placeholder={slot.type === 'choice' ? 'Comma-separated values' : undefined}
              />
            );
          }
          return (
            <div key={id} className="uni-filter-slot">
              <div className="uni-card__grid">{fields}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function slotTabLabel(slot, idx) {
  return slot.label || slot.id || `Filter ${idx + 1}`;
}

function FilterSlotFields({
  slot, idx, disabled, catalog, dateFields, update,
}) {
  return (
    <div className="uni-card__grid">
      <UniTextField
        label="Slot id"
        value={slot.id}
        disabled={disabled}
        onChange={(v) => update(idx, { id: v.replace(/\s+/g, '_') })}
      />
      <UniSelectField
        label="Type"
        value={slot.type}
        disabled={disabled}
        onChange={(v) => update(idx, { type: v })}
        options={FILTER_SLOT_TYPE_OPTIONS}
      />
      <UniTextField
        label="Label"
        value={slot.label || ''}
        disabled={disabled}
        onChange={(v) => update(idx, { label: v })}
      />
      {slot.type === 'date' && (
        <UniSelectField
          label="Filter on column"
          value={dateFieldValueForSlot(slot)}
          disabled={disabled}
          onChange={(v) => {
            const picked = dateFields.find((f) => f.value === v);
            const patch = {
              field: v,
              column_key: picked?.question_id ? `q:${picked.question_id}` : (
                v === 'submitted_at' ? 'submitted_at' : v
              ),
            };
            if (picked?.question_id) {
              patch.question_id = picked.question_id;
            } else {
              patch.question_id = undefined;
            }
            update(idx, patch);
          }}
          options={dateFields.map((f) => ({
            value: f.value,
            label: f.label,
          }))}
        />
      )}
      {slot.type === 'user_attr' && (
        <UniSelectField
          label="User field"
          value={slot.field || 'city'}
          disabled={disabled}
          onChange={(v) => update(idx, { field: v, column_key: `user.${v}` })}
          options={(catalog?.user_attributes || []).map((a) => ({
            value: a.field, label: a.label,
          }))}
        />
      )}
      {(slot.type === 'choice' || slot.type === 'text' || slot.type === 'numeric_range') && (
        <UniSelectField
          label="Question"
          value={slot.question_id || ''}
          disabled={disabled}
          onChange={(v) => update(idx, {
            question_id: v,
            column_key: v ? `q:${v}` : slotColumnKey(slot),
          })}
          options={[
            { value: '', label: '— pick —' },
            ...((slot.type === 'choice'
              ? catalog?.choice_questions
              : slot.type === 'numeric_range'
                ? catalog?.numeric_questions
                : catalog?.choice_questions?.concat(catalog?.date_questions || []) || []
            ) || []).map((q) => ({ value: q.id, label: q.text })),
          ]}
        />
      )}
      {slot.type === 'text' && (
        <UniSelectField
          label="Match"
          value={slot.op || 'contains'}
          disabled={disabled}
          onChange={(v) => update(idx, { op: v })}
          options={[
            { value: 'contains', label: 'Contains' },
            { value: 'exact', label: 'Exact' },
          ]}
        />
      )}
      {slot.type === 'date' && (
        <UniCheckField
          label="Single day only (matrix reports)"
          checked={!!slot.single_day}
          disabled={disabled}
          onChange={(v) => update(idx, { single_day: v })}
        />
      )}
    </div>
  );
}

export function FilterSlotBuilder({
  slots, onChange, catalog, disabled, title, subtitle,
}) {
  const list = slots || [];
  const [columnPick, setColumnPick] = React.useState('');
  const [activeIdx, setActiveIdx] = React.useState(0);

  React.useEffect(() => {
    if (list.length === 0) {
      setActiveIdx(0);
      return;
    }
    if (activeIdx > list.length - 1) {
      setActiveIdx(list.length - 1);
    }
  }, [list.length, activeIdx]);

  const addSlot = (type) => {
    const id = `${type}_${Math.random().toString(36).slice(2, 7)}`;
    const next = { id, type, label: type.replace(/_/g, ' ') };
    if (type === 'date') {
      Object.assign(next, {
        field: 'submitted_at',
        id: list.some((s) => s.id === 'period') ? id : 'period',
        label: 'Period',
        column_key: 'submitted_at',
      });
    }
    if (type === 'user_attr') Object.assign(next, { field: 'city', column_key: 'user.city' });
    if (type === 'text') Object.assign(next, { op: 'contains' });
    onChange([...list, next]);
    setActiveIdx(list.length);
  };

  const addColumnFilter = (columnKey) => {
    if (!columnKey) return;
    const col = (catalog?.filterable_columns || []).find((c) => c.column_key === columnKey);
    if (!col) return;
    const existingIds = list.map((s) => s.id);
    onChange([...list, slotFromFilterableColumn(col, existingIds)]);
    setColumnPick('');
    setActiveIdx(list.length);
  };

  const update = (idx, patch) => {
    onChange(list.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const remove = (idx) => {
    onChange(list.filter((_, i) => i !== idx));
    setActiveIdx((cur) => {
      if (list.length <= 1) return 0;
      if (idx < cur) return cur - 1;
      if (idx === cur) return Math.max(0, idx - 1);
      return cur;
    });
  };

  const filterableColumns = catalog?.filterable_columns || [];
  const dateFields = dateFieldOptions(catalog);
  const safeIdx = list.length ? Math.min(activeIdx, list.length - 1) : 0;
  const activeSlot = list[safeIdx];

  return (
    <div className="uni-card" style={{ marginTop: title ? 0 : 12 }}>
      <div className="uni-card__title">{title || 'Filter slots (template)'}</div>
      <div className="uni-card__sub">
        {subtitle || (
          'Viewers fill values at run time to filter which rows appear. '
          + 'Use a form date column to filter by the date entered in the form '
          + '(not when it was submitted).'
        )}
      </div>

      {!disabled && filterableColumns.length > 0 && (
        <div className="uni-card__grid" style={{ marginTop: 8 }}>
          <UniSelectField
            label="Add filter by column"
            value={columnPick}
            disabled={disabled}
            onChange={(v) => {
              setColumnPick(v);
              if (v) addColumnFilter(v);
            }}
            options={[
              { value: '', label: '— pick a column —' },
              ...filterableColumns.map((c) => ({
                value: c.column_key,
                label: `${c.label} (${c.filter_type.replace(/_/g, ' ')})`,
              })),
            ]}
          />
        </div>
      )}

      {list.length > 0 && (
        <div className="uni-filter-slot-tabs-wrap">
          <div className="uni-filter-slot-tabs" role="tablist" aria-label="Runtime filter slots">
            {list.map((slot, idx) => (
              <button
                key={slot.id || idx}
                type="button"
                role="tab"
                id={`uni-filter-slot-tab-${idx}`}
                aria-selected={idx === safeIdx}
                aria-controls={`uni-filter-slot-panel-${idx}`}
                className={
                  'uni-filter-slot-tab'
                  + (idx === safeIdx ? ' uni-filter-slot-tab--active' : '')
                }
                onClick={() => setActiveIdx(idx)}
              >
                <span className="uni-filter-slot-tab__label">{slotTabLabel(slot, idx)}</span>
                <span className="uni-filter-slot-tab__type">
                  {(slot.type || 'filter').replace(/_/g, ' ')}
                </span>
              </button>
            ))}
          </div>
          {activeSlot && (
            <div
              className="uni-filter-slot uni-filter-slot--panel"
              role="tabpanel"
              id={`uni-filter-slot-panel-${safeIdx}`}
              aria-labelledby={`uni-filter-slot-tab-${safeIdx}`}
            >
              <div className="uni-filter-slot__head">
                <div>
                  <div className="uni-filter-slot__title">
                    {slotTabLabel(activeSlot, safeIdx)}
                  </div>
                  <div className="uni-filter-slot__meta">
                    {`Slot ${safeIdx + 1}`}
                    {activeSlot.type ? ` · ${activeSlot.type.replace(/_/g, ' ')}` : ''}
                    {activeSlot.id ? ` · ${activeSlot.id}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="uni-row-btn"
                  disabled={disabled}
                  onClick={() => remove(safeIdx)}
                >
                  Remove
                </button>
              </div>
              <FilterSlotFields
                slot={activeSlot}
                idx={safeIdx}
                disabled={disabled}
                catalog={catalog}
                dateFields={dateFields}
                update={update}
              />
            </div>
          )}
        </div>
      )}
      {!disabled && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {FILTER_SLOT_TYPE_OPTIONS.map((o) => (
            <button key={o.value} type="button" className="uni-row-btn"
                    onClick={() => addSlot(o.value)}>
              + {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
